import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import LightGestion from 'components/headers/lightGestion';
import { useTranslation } from 'react-i18next';

// currency is localized inside the component using i18n

const clamp = (n, min=0, max=100) => Math.max(min, Math.min(max, n));

const Card = ({ title, value, color }) => (
    <div style={{ flex: 1, background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 10px 24px rgba(15,23,42,0.06)', border: '1px solid #eef2f7', minWidth: 240 }}>
        <div style={{ color: '#6b7280', fontWeight: 700, marginBottom: 6 }}>{title}</div>
        <div style={{ fontWeight: 800, fontSize: 36, color }}>{value}</div>
    </div>
);

const RankRow = ({ index, name, category, rightText, barColor = '#22c55e', percent=0 }) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: '1px solid #eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#2563eb' }}>{index}</div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div style={{ color: '#6b7280', fontSize: 13 }}>{category}</div>
            </div>
        </div>

        <div style={{ fontWeight: 800, color: '#16a34a' }}>{rightText}</div>
    </div>
);

const ProgressRow = ({ index, name, category, rightTop, rightBottom, progressColor = '#7c3aed', percent=0 }) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: '1px solid #eef2f7' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef2ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{index}</div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>{category}</div>
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, color: '#1d4ed8' }}>{rightTop}</div>
                <div style={{ color: '#6b7280', fontSize: 12 }}>{rightBottom}</div>
            </div>
        </div>
        <div style={{ marginTop: 8, height: 10, background: '#eef2f7', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ width: `${clamp(percent)}%`, height: '100%', background: progressColor }} />
        </div>
    </div>
);

const extractList = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    const { data, items, results, list } = payload;
    if (Array.isArray(items)) {
        return items;
    }
    if (Array.isArray(results)) {
        return results;
    }
    if (Array.isArray(list)) {
        return list;
    }
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.items)) {
        return data.items;
    }
    if (Array.isArray(data?.data)) {
        return data.data;
    }
    if (Array.isArray(payload?.items?.data)) {
        return payload.items.data;
    }
    if (Array.isArray(payload?.data?.items?.data)) {
        return payload.data.items.data;
    }
    return [];
};

const Dashbord = () => {
    const { t } = useTranslation();
    const [overview, setOverview] = useState({ revenue: 0, netProfit: 0, credits: 0 });
    const [profitable, setProfitable] = useState([]);
    const [topSold, setTopSold] = useState([]);
    const [loading, setLoading] = useState(true);

    const currency = useCallback((v) => `${Number(v || 0).toFixed(2)} ${t('stock.currency','MAD')}`,[t]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        try {
            const [ovr, prf, ts] = await Promise.allSettled([
                axios.get('https://plateformewmg.cloud/api/Gestion/overview', { headers }),
                axios.get('https://plateformewmg.cloud/api/Gestion/top-profitable?limit=5', { headers }),
                axios.get('https://plateformewmg.cloud/api/Gestion/top-sold?limit=5', { headers }),
            ]);
            console.log("overview",ovr, "profitable", prf, "topSold", ts);
            if (ovr.status === 'fulfilled') {
                const d = ovr.value.data || {};
                setOverview({
                    revenue: Number(d.revenue ?? d.total_sales ?? d.ca ?? 0),
                    netProfit: Number(d.net_profit ?? d.benefit ?? d.profit ?? 0),
                    credits: Number(d.credits ?? d.credit ?? 0),
                });
            }

            if (prf.status === 'fulfilled') {
                let list = extractList(prf.value?.data);
                if (!list.length) {
                    list = extractList(prf.value);
                }
                setProfitable(list.map((x) => ({
                    name: x.name?.fr || x.name || x.Name?.fr || x.Name || '—',
                    category: x.category || x.CategoryAdmin || x.category_admin || '',
                    profit: Number(x.profit ?? 0),
                    margin: Number(x.margin ?? x.margin_percent ?? 0),
                    sold: Number(x.sold ?? x.sales ?? 0),
                    revenue: Number(x.revenue ?? x.ca ?? 0),
                })));
            }

            if (ts.status === 'fulfilled') {
                let list = extractList(ts.value?.data);
                if (!list.length) {
                    list = extractList(ts.value);
                }
                setTopSold(list.map((x) => ({
                    name: x.name?.fr || x.name || x.Name?.fr || x.Name || '—',
                    category: x.category || x.CategoryAdmin || x.category_admin || '',
                    sold: Number(x.sold ?? x.sales ?? 0),
                    perDay: Number(x.per_day ?? x.daily ?? 0),
                    revenue: Number(x.revenue ?? x.ca ?? 0),
                })));
            }
        } catch (e) {
            console.error('Analytics fetch error', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const maxProfit = Math.max(1, ...profitable.map(p => p.profit));
    const maxRevenueSold = Math.max(1, ...topSold.map(p => p.revenue));

    const exportCSV = () => {
        const rows = [
            [
                t('analytics.csv.section','Section'),
                t('analytics.csv.name','Nom'),
                t('analytics.csv.category','Catégorie'),
                t('analytics.csv.sales','Ventes'),
                t('analytics.csv.revenue',"Chiffre d'Affaires"),
                t('analytics.csv.profit','Profit'),
                t('analytics.csv.margin','Marge %')
            ],
            ...profitable.map((p) => [t('analytics.csv.sections.profitable','Plus Rentables'), p.name, p.category, p.sold, p.revenue, p.profit, p.margin]),
            ...topSold.map((p) => [t('analytics.csv.sections.topSold','Plus Vendus'), p.name, p.category, p.sold, p.revenue, '', '']),
        ];
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = t('analytics.csv.filename','analytics.csv');
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <AnimationRevealPage>
            <LightGestion />

            <div className="container" style={{ padding: 1,marginTop:"-50px" }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Card title={t('analytics.cards.revenue', "Chiffre d'Affaires (Ventes)")} value={currency(overview.revenue)} color="#16a34a" />
                    <Card title={t('analytics.cards.netProfit', 'Bénéfice Net')} value={currency(overview.netProfit)} color="#2563eb" />
                    <Card title={t('analytics.cards.credits', 'Crédits Accordés')} value={currency(overview.credits)} color="#f97316" />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
                    <h3 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span role="img" aria-label="chart">📊</span> {t('analytics.title', 'Analytics Produits')}
                    </h3>
                    <button onClick={exportCSV} className="btn" style={{ background: '#1e64f2', color: '#fff', padding: '10px 18px', borderRadius: 10 }}>{t('analytics.export', 'Exporter')}</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 12 }}>
                    <div style={{ background: '#eafff0', borderRadius: 14, padding: 16, border: '1px solid #dcfce7' }}>
                        <h5 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>💰 {t('analytics.profitable.title', 'Produits les Plus Rentables')}</h5>
                        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                            {profitable.length === 0 && (
                                <div style={{ color: '#6b7280' }}>{loading ? t('analytics.loading','Chargement...') : t('analytics.noData','Aucune donnée')}</div>
                            )}
                            {profitable.map((p, i) => (
                                <div key={`${p.name}-${i}`} style={{ display: 'grid', gap: 8 }}>
                                    <RankRow index={i + 1} name={p.name} category={p.category} rightText={currency(p.profit)} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: 13 }}>
                                        <div>{p.sold} {t('analytics.soldLabel','vendus')}</div>
                                        <div>{currency(p.revenue)} {t('analytics.caShort','CA')}</div>
                                        <div>{(Math.round(p.margin * 100) / 100)}% {t('analytics.marginShort','marge')}</div>
                                    </div>
                                    <div style={{ height: 10, background: '#e2fbe8', borderRadius: 6, overflow: 'hidden' }}>
                                        <div style={{ width: `${clamp((p.profit / maxProfit) * 100)}%`, background: '#22c55e', height: '100%' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ background: '#eef2ff', borderRadius: 14, padding: 16, border: '1px solid #e9e5ff' }}>
                        <h5 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>🔥 {t('analytics.topSold.title', 'Produits les Plus Vendus')}</h5>
                        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                            {topSold.length === 0 && (
                                <div style={{ color: '#6b7280' }}>{loading ? t('analytics.loading','Chargement...') : t('analytics.noData','Aucune donnée')}</div>
                            )}
                            {topSold.map((p, i) => (
                                <ProgressRow
                                    key={`${p.name}-${i}`}
                                    index={i + 1}
                                    name={p.name}
                                    category={p.category}
                                    rightTop={p.sold}
                                    rightBottom={`${currency(p.revenue)} ${t('analytics.caShort','CA')}`}
                                    progressColor="#7c3aed"
                                    percent={clamp((p.revenue / maxRevenueSold) * 100)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

        </AnimationRevealPage>
    );
};

export default Dashbord;
